import React from "react";
import { supabase } from "@/integrations/supabase/client";
import { isTireCategory, normalizeExpenseCategory } from "@/utils/category-helpers";
import type {
  DocumentItem,
  ExpenseItem,
  FuelItem,
  MaintenanceItem,
  MaintPlanItem,
  TireItem,
  TripItem,
  VehicleDetailData,
  VehicleSummary,
} from "@/types/fleet-reports.v2";
import { formatWorkOrderNumber } from "@/types/fleet-reports.v2";

type Period = { from: Date; to: Date };

const DEFAULT_LIMIT = 500;

function toPtBRDateTime(v: any): string {
  if (!v) return "-";
  try {
    const d = new Date(v);
    return d.toLocaleString("pt-BR");
  } catch {
    return String(v);
  }
}

function toPtBRDate(v: any): string {
  if (!v) return "-";
  try {
    const d = new Date(v);
    return d.toLocaleDateString("pt-BR");
  } catch {
    return String(v);
  }
}

function asNumber(n: any): number {
  const x = Number(n);
  return Number.isFinite(x) ? x : 0;
}

export async function fetchVehicleDetail(
  vehicleId: string,
  period: Period,
  summaryFallback: VehicleSummary,
  limit: number = DEFAULT_LIMIT
): Promise<VehicleDetailData> {
  const fromISO = period.from.toISOString();
  const toISO = period.to.toISOString();

  const fuelQ = supabase
    .from("fleet_fuel_logs")
    .select(`
      id,
      occurred_at,
      station_id,
      liters,
      price_unit,
      odometer,
      total_value,
      fuel_type,
      fleet_providers:station_id ( name )
    `)
    .eq("vehicle_id", vehicleId)
    .gte("occurred_at", fromISO)
    .lte("occurred_at", toISO)
    .order("occurred_at", { ascending: false })
    .limit(limit);

  const maintQ = supabase
    .from("fleet_work_orders")
    .select(`
      id,
      description,
      status,
      total_cost,
      opened_at,
      closed_at,
      plan_id,
      order_number,
      provider_id,
      fleet_maint_plans:plan_id ( name, type ),
      fleet_providers:provider_id ( name )
    `)
    .eq("vehicle_id", vehicleId)
    .gte("opened_at", fromISO)
    .lte("opened_at", toISO)
    .order("opened_at", { ascending: false })
    .limit(limit);

  const plansQ = supabase
    .from("fleet_maint_plans")
    .select(`
      id,
      name,
      type,
      status,
      approval_status,
      approved_at,
      maintenance_category,
      periodicity_km,
      periodicity_days,
      next_execution_km,
      next_execution_date
    `)
    .eq("vehicle_id", vehicleId)
    .order("approval_status", { ascending: true })
    .order("status", { ascending: true })
    .limit(limit);

  const expensesQ = supabase
    .from("fleet_expenses")
    .select(`
      id,
      due_date,
      description,
      value,
      notes,
      status,
      fleet_expense_categories:category_id ( name )
    `)
    .eq("vehicle_id", vehicleId)
    .gte("due_date", fromISO.split("T")[0])
    .lte("due_date", toISO.split("T")[0])
    .order("due_date", { ascending: false })
    .limit(limit);

  const docsQ = supabase
    .from("fleet_documents")
    .select("id, name, document_type, issue_date, expiry_date, status")
    .eq("vehicle_id", vehicleId)
    .order("expiry_date", { ascending: true })
    .limit(limit);

  const tripsQ = supabase
    .from("fleet_trips")
    .select(`
      id,
      start_at,
      eta_start,
      end_at,
      eta_end,
      distance_actual_km,
      distance_planned_km,
      origin,
      destination,
      status,
      driver_id,
      fleet_drivers:driver_id ( name )
    `)
    .eq("vehicle_id", vehicleId)
    .gte("eta_start", fromISO)
    .lte("eta_start", toISO)
    .order("eta_start", { ascending: false })
    .limit(limit);

  const [fuelR, maintR, plansR, expR, docsR, tripsR] = await Promise.all([
    fuelQ,
    maintQ,
    plansQ,
    expensesQ,
    docsQ,
    tripsQ,
  ]);

  const fuelRows = (fuelR.data ?? []) as any[];
  const maintRows = (maintR.data ?? []) as any[];
  const plansRows = (plansR.data ?? []) as any[];
  const expRows = (expR.data ?? []) as any[];
  const docsRows = (docsR.data ?? []) as any[];
  const tripsRows = (tripsR.data ?? []) as any[];

  const fuel: FuelItem[] = fuelRows.map((f) => {
    const liters = f.liters != null ? asNumber(f.liters) : null;
    const priceUnit = f.price_unit != null ? asNumber(f.price_unit) : null;
    const totalValue =
      f.total_value != null
        ? asNumber(f.total_value)
        : liters != null && priceUnit != null
        ? liters * priceUnit
        : null;

    return {
      id: String(f.id),
      occurredAt: toPtBRDate(f.occurred_at),
      station: f.fleet_providers?.name ?? "-",
      liters,
      priceUnit,
      totalValue,
      odometerKm: f.odometer != null ? asNumber(f.odometer) : null,
      notes: null,
    };
  });

  const maintenance: MaintenanceItem[] = maintRows.map((m) => {
    const executedAt = m.closed_at ? toPtBRDate(m.closed_at) : toPtBRDate(m.opened_at);
    const orderNum =
      m.order_number != null
        ? `OS-${String(m.order_number).padStart(4, "0")}`
        : formatWorkOrderNumber(String(m.id));

    return {
      id: String(m.id),
      planId: m.plan_id ?? null,
      orderNumber: orderNum,
      planName: m.fleet_maint_plans?.name ?? "Avulsa",
      executedAt,
      description: m.description ?? null,
      supplier: m.fleet_providers?.name ?? "-",
      type: m.fleet_maint_plans?.type ?? "other",
      status: m.status ?? null,
      items: undefined,
      totalCost: asNumber(m.total_cost),
      notes: m.description ?? null,
    };
  });

  const maintPlans: MaintPlanItem[] = plansRows.map((p) => ({
    id: String(p.id),
    name: p.name ?? "-",
    type: p.type ?? "other",
    status: p.status ?? "active",
    approvalStatus: p.approval_status ?? null,
    approvedAt: p.approved_at ? toPtBRDate(p.approved_at) : null,
    category: p.maintenance_category ?? null,
    periodicityKm: p.periodicity_km != null ? asNumber(p.periodicity_km) : null,
    periodicityDays: p.periodicity_days != null ? asNumber(p.periodicity_days) : null,
    nextExecutionKm: p.next_execution_km != null ? asNumber(p.next_execution_km) : null,
    nextExecutionDate: p.next_execution_date ? toPtBRDate(p.next_execution_date) : null,
  }));

  const tires: TireItem[] = [];
  const expenses: ExpenseItem[] = [];

  for (const e of expRows) {
    const catName = e.fleet_expense_categories?.name ?? e.description ?? "Outros";
    const cat = normalizeExpenseCategory(catName);

    const row = {
      id: String(e.id),
      occurredAt: toPtBRDate(e.due_date),
      supplier: "-",
      totalCost: asNumber(e.value),
      status: e.status ?? null,
      notes: e.notes ?? null,
    };

    if (isTireCategory(cat)) {
      tires.push({
        ...row,
        tireModel: e.description ?? "-",
        description: e.notes ?? null,
        qty: null,
      });
    } else {
      expenses.push({
        id: row.id,
        occurredAt: row.occurredAt,
        category: String(cat),
        description: e.description ?? null,
        supplier: row.supplier,
        totalCost: row.totalCost,
        status: row.status,
        notes: row.notes,
      });
    }
  }

  const documents: DocumentItem[] = docsRows.map((d) => ({
    id: String(d.id),
    name: d.name ?? "-",
    type: d.document_type ?? "-",
    issuedAt: d.issue_date ? toPtBRDate(d.issue_date) : null,
    expiryDate: d.expiry_date ? toPtBRDate(d.expiry_date) : null,
    paidAt: null,
    totalCost: null,
    status: d.status ?? null,
  }));

  const trips: TripItem[] = tripsRows.map((t) => ({
    id: String(t.id),
    startAt: toPtBRDateTime(t.start_at || t.eta_start),
    endAt: t.end_at || t.eta_end ? toPtBRDateTime(t.end_at || t.eta_end) : null,
    distanceKm: asNumber(t.distance_actual_km || t.distance_planned_km),
    driverName: t.fleet_drivers?.name ?? "-",
    origin: t.origin ?? null,
    destination: t.destination ?? null,
    status: t.status ?? null,
    notes: null,
  }));

  return {
    ...summaryFallback,
    fuel,
    maintenance,
    maintPlans,
    tires,
    expenses,
    documents,
    trips,
  };
}

type UseAllVehiclesDetailsResult = {
  allVehiclesDetails: Map<string, VehicleDetailData>;
  isLoadingDetails: boolean;
  error: string | null;
};

export function useAllVehiclesDetails(
  vehiclesSummary: VehicleSummary[],
  period: Period | undefined
): UseAllVehiclesDetailsResult {
  const [allVehiclesDetails, setAllVehiclesDetails] = React.useState<Map<string, VehicleDetailData>>(new Map());
  const [isLoadingDetails, setIsLoadingDetails] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const vehicleIds = React.useMemo(() => vehiclesSummary.map((v) => v.vehicle_id).join(","), [vehiclesSummary]);
  const periodKey = React.useMemo(() => {
    if (!period) return "";
    return `${period.from.toISOString()}|${period.to.toISOString()}`;
  }, [period]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadAll() {
      if (!period || vehiclesSummary.length === 0) {
        setAllVehiclesDetails(new Map());
        setIsLoadingDetails(false);
        setError(null);
        return;
      }

      setIsLoadingDetails(true);
      setError(null);

      try {
        // Carregar em batches de 5 para não sobrecarregar
        const BATCH_SIZE = 5;
        const results = new Map<string, VehicleDetailData>();

        for (let i = 0; i < vehiclesSummary.length; i += BATCH_SIZE) {
          if (cancelled) return;

          const batch = vehiclesSummary.slice(i, i + BATCH_SIZE);
          const batchResults = await Promise.all(
            batch.map((v) => fetchVehicleDetail(v.vehicle_id, period, v))
          );

          batchResults.forEach((detail, idx) => {
            results.set(batch[idx].vehicle_id, detail);
          });

          // Atualizar progressivamente
          if (!cancelled) {
            setAllVehiclesDetails(new Map(results));
          }
        }

        if (!cancelled) {
          setIsLoadingDetails(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.message ?? "Erro ao carregar detalhes dos veículos");
          setIsLoadingDetails(false);
        }
      }
    }

    loadAll();

    return () => {
      cancelled = true;
    };
  }, [vehicleIds, periodKey, vehiclesSummary, period]);

  return { allVehiclesDetails, isLoadingDetails, error };
}
